import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { listCompanyUsers, getCalendar, getTimesheet, listFiredForPeriod, listBonuses, listAllowances, listWithholdings, listRecurringWithholdings, exportPayroll, listLocations } from "../api";
import { ROLES, statusesAsList, countWorkDays } from "../constants";
import { computeStats, calcSalary } from "../controllers/salary";
import { computeTaxes, listTaxes, userMonthlyGross, userEffectiveTaxConfig } from "../controllers/taxes";
import { aggregateExtras, activeAllowancesInMonth } from "../controllers/extras";
import { aggregateWithholdings, mergeWithholdingsForMonth } from "../controllers/withholdings";
import { todayParts } from "../utils/dates";
import { monthWeekRanges, calendarDaysSubset } from "../utils/weeks";
import LocationFilter, { applyLocationFilter } from "../components/LocationFilter";
import AppPage from "../components/AppPage";

const MONTH_KEYS = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
const fmt = (n) => Number(n || 0).toLocaleString("ru-RU", { maximumFractionDigits: 2 });

export default function Payroll() {
  const { t } = useTranslation();
  const { user, company } = useAuth();
  const tp = todayParts();
  const [year, setYear] = useState(tp.year);
  const [month, setMonth] = useState(tp.month);
  const [users, setUsers] = useState([]);
  const [firedUsers, setFiredUsers] = useState([]);
  const [showFired, setShowFired] = useState(false);
  const [calendar, setCalendar] = useState({ days: {} });
  const [timesheet, setTimesheet] = useState({ entries: {} });
  const [bonuses, setBonuses] = useState([]);
  const [allowances, setAllowances] = useState([]);
  const [withholdings, setWithholdings] = useState([]);
  const [recurringWithholdings, setRecurringWithholdings] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationFilter, setLocationFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [weeklyView, setWeeklyView] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const [u, cal, ts, bs, als, wh, rwh, locs] = await Promise.all([
        listCompanyUsers(),
        getCalendar(year, month),
        getTimesheet(year, month),
        listBonuses({ year, month }).catch(() => []),
        listAllowances({ status: "active", active_year: year, active_month: month }).catch(() => []),
        listWithholdings({ year, month }).catch(() => []),
        listRecurringWithholdings({ status: "active" }).catch(() => []),
        listLocations().catch(() => []),
      ]);
      if (cancelled) return;
      setUsers(u); setCalendar(cal); setTimesheet(ts);
      setBonuses(bs || []); setAllowances(als || []);
      setWithholdings(wh || []); setRecurringWithholdings(rwh || []); setLocations(locs || []);
      setLoading(false);
      if (user && [ROLES.DIRECTOR, ROLES.HR].includes(user.role)) {
        try {
          const fired = await listFiredForPeriod(year, month);
          if (!cancelled) setFiredUsers(fired || []);
        } catch { if (!cancelled) setFiredUsers([]); }
      } else {
        setFiredUsers([]);
      }
    })();
    return () => { cancelled = true; };
  }, [year, month, user]);

  const isPrivileged = user && [ROLES.DIRECTOR, ROLES.HR].includes(user.role);
  const [exporting, setExporting] = useState(null);
  const onExport = async (fmt) => {
    setExporting(fmt);
    try { await exportPayroll(year, month, fmt, { weekly: weeklyView }); }
    catch (e) { alert(e?.response?.data?.detail || e.message); }
    finally { setExporting(null); }
  };
  const visibleUsers = applyLocationFilter(
    isPrivileged ? users : users.filter(u => u.user_id === user?.user_id),
    locationFilter,
  );
  const norm = countWorkDays(calendar.days);
  const statuses = useMemo(() => statusesAsList(company), [company]);

  const enabledTaxes = useMemo(() => listTaxes(company?.tax_config), [company]);
  const employeeTaxes = enabledTaxes.filter(x => x.apply_to !== "employer");
  const employerTaxes = enabledTaxes.filter(x => x.apply_to === "employer");
  const taxKeys = enabledTaxes.map(x => x.key);

  const buildRow = (u, calDays = calendar.days) => {
    const stats = computeStats(timesheet.entries, u.user_id, calDays, statuses);
    const ucfg = userEffectiveTaxConfig(u, company, company?.tax_config);
    const uEnabledTaxes = listTaxes(ucfg);
    const uTaxKeys = uEnabledTaxes.map(x => x.key);
    const monthlyGross = userMonthlyGross(u, ucfg, null, { year });
    const { dailyRate, gross } = calcSalary({
      monthlySalary: monthlyGross, normWorkDays: norm, paidDays: stats.paidDays, fixedTotal: stats.fixedTotal,
    });
    const userBonuses = bonuses.filter(b => b.user_id === u.user_id);
    const userAllowances = activeAllowancesInMonth(allowances, year, month).filter(a => a.user_id === u.user_id);
    const bonusAgg = aggregateExtras(userBonuses, monthlyGross, gross, uTaxKeys, ucfg, u, { year });
    const allowAgg = aggregateExtras(userAllowances, monthlyGross, gross, uTaxKeys, ucfg, u, { year });
    const userWithholdings = mergeWithholdingsForMonth(
      withholdings.filter(w => w.user_id === u.user_id),
      recurringWithholdings,
      year,
      month,
    );
    const whAgg = aggregateWithholdings(userWithholdings, monthlyGross, gross);
    const taxableByKey = {};
    for (const k of uTaxKeys) {
      taxableByKey[k] = (bonusAgg.taxableByKey[k] || 0) + (allowAgg.taxableByKey[k] || 0) - whAgg.preTaxTotal;
    }
    const extras = { total: bonusAgg.total + allowAgg.total - whAgg.preTaxTotal, taxableByKey };
    const tax = computeTaxes(gross, ucfg, extras, { year });
    const netToPay = Math.max(0, tax.net - whAgg.postTaxTotal);
    return {
      u, stats, dailyRate, gross, tax, bonusTotal: bonusAgg.total, allowanceTotal: allowAgg.total,
      withholdingTotal: whAgg.total, netToPay,
    };
  };

  const rows = useMemo(() => visibleUsers.map(u => buildRow(u)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleUsers, timesheet, calendar, norm, company, statuses, bonuses, allowances, withholdings, recurringWithholdings, year, month]);
  const firedRows = useMemo(() => firedUsers.map(u => buildRow(u)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [firedUsers, timesheet, calendar, norm, company, statuses, bonuses, allowances, withholdings, recurringWithholdings, year, month]);

  const weeks = useMemo(() => monthWeekRanges(year, month), [year, month]);

  const weekBlocks = useMemo(() => {
    if (!weeklyView) return [];
    return weeks.map((w, idx) => {
      const subset = calendarDaysSubset(calendar.days, w.dayKeys);
      const weekRows = visibleUsers.map(u => {
        const stats = computeStats(timesheet.entries, u.user_id, subset, statuses);
        const ucfg = userEffectiveTaxConfig(u, company, company?.tax_config);
        const monthlyGross = userMonthlyGross(u, ucfg, null, { year });
        const { dailyRate, gross } = calcSalary({
          monthlySalary: monthlyGross, normWorkDays: norm, paidDays: stats.paidDays, fixedTotal: stats.fixedTotal,
        });
        return { u, stats, dailyRate, gross };
      });
      return { ...w, index: idx + 1, rows: weekRows };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeklyView, weeks, visibleUsers, timesheet, calendar, statuses, company, norm, year]);

  const totals = useMemo(() => {
    const acc = { gross: 0, bonus: 0, allowance: 0, withholding: 0, totalGross: 0, net: 0, byTax: {} };
    for (const k of taxKeys) acc.byTax[k] = 0;
    for (const r of rows) {
      acc.gross += r.tax.gross;
      acc.bonus += r.bonusTotal;
      acc.allowance += r.allowanceTotal;
      acc.withholding += r.withholdingTotal;
      acc.totalGross += r.tax.totalGross;
      acc.net += r.netToPay;
      for (const k of taxKeys) acc.byTax[k] += (r.tax.byTax[k]?.amount || 0);
    }
    return acc;
  }, [rows, taxKeys]);

  const tableProps = {
    t, company, employeeTaxes, employerTaxes, isPrivileged, totals, fmt,
  };

  return (
    <AppPage
      testId="payroll-page"
      bodyScroll="table"
      toolbar={(
        <>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h4 className="fw-bold mb-0">{t("payroll.title")}</h4>
        <div className="d-flex gap-2 flex-wrap align-items-center">
          {isPrivileged && (
            <LocationFilter locations={locations} users={isPrivileged ? users : []}
                            value={locationFilter} onChange={setLocationFilter}
                            testid="payroll-location-filter" />
          )}
          <div className="form-check mb-0" title={t("payroll.weekly_hint")}>
            <input
              className="form-check-input"
              type="checkbox"
              id="payroll-weekly"
              checked={weeklyView}
              onChange={(e) => setWeeklyView(e.target.checked)}
              data-testid="payroll-weekly-toggle"
            />
            <label className="form-check-label small" htmlFor="payroll-weekly">
              {t("payroll.weekly")}
            </label>
          </div>
          {isPrivileged && (
            <>
              <button className="btn btn-outline-success btn-sm" onClick={() => onExport("xlsx")}
                      disabled={!!exporting || loading}
                      data-testid="payroll-export-xlsx">
                {exporting === "xlsx"
                  ? <><span className="spinner-border spinner-border-sm me-1"></span>{t("common.loading")}</>
                  : <><i className="bi bi-file-earmark-excel me-1"></i>Excel</>}
              </button>
              <button className="btn btn-outline-danger btn-sm" onClick={() => onExport("pdf")}
                      disabled={!!exporting || loading}
                      data-testid="payroll-export-pdf">
                {exporting === "pdf"
                  ? <><span className="spinner-border spinner-border-sm me-1"></span>{t("common.loading")}</>
                  : <><i className="bi bi-file-earmark-pdf me-1"></i>PDF</>}
              </button>
            </>
          )}
          <select className="form-select" style={{ width: 160 }} value={month}
                  onChange={(e) => setMonth(Number(e.target.value))}>
            {MONTH_KEYS.map((k, i) => <option key={i} value={i}>{t(`months.${k}`)}</option>)}
          </select>
          <select className="form-select" style={{ width: 110 }} value={year}
                  onChange={(e) => setYear(Number(e.target.value))}>
            {[year - 1, year, year + 1].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      <div className="card card-soft mb-3"><div className="card-body small">
        <strong>{t("payroll.norm_days")}:</strong> {norm}
        {enabledTaxes.length > 0 && (
          <> · {enabledTaxes.map(x => `${x.label} ${x.rate}%`).join(" · ")}</>
        )}
        {weeklyView && (
          <div className="text-muted mt-1">{t("payroll.weekly_hint")}</div>
        )}
      </div></div>
        </>
      )}
    >
      {loading ? (
        <div className="card card-soft">
          <div className="text-center text-muted py-5" data-testid="payroll-loading">
            <div className="spinner-border spinner-border-sm me-2"></div>
            {t("payroll.loading")}
          </div>
        </div>
      ) : weeklyView ? (
        <>
          {weekBlocks.map((w) => (
            <div className="card card-soft mb-3" key={`${w.start}-${w.end}`} data-testid={`payroll-week-${w.index}`}>
              <div className="card-header bg-light py-2">
                <strong>
                  {t("payroll.week_title", {
                    n: w.index,
                    from: w.start,
                    to: w.end,
                    month: t(`months_of.${MONTH_KEYS[month]}`),
                  })}
                </strong>
              </div>
              <div className="table-responsive">
                <table className="table align-middle mb-0 tabel-compact tabel-compact-tight payroll-nowrap">
                  <thead className="table-light">
                    <tr>
                      <th>{t("payroll.table.employee")}</th>
                      <th className="text-end">{t("payroll.table.worked_days")}</th>
                      <th className="text-end">{t("payroll.table.daily_rate")}</th>
                      <th className="text-end">{t("payroll.week_gross_only")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {w.rows.map(({ u, stats, dailyRate, gross }) => (
                      <tr key={u.user_id}>
                        <td>{u.name}</td>
                        <td className="text-end">{stats.paidDays}</td>
                        <td className="text-end">{fmt(dailyRate)}</td>
                        <td className="text-end fw-semibold">{fmt(gross)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          <div className="card card-soft mb-3" data-testid="payroll-week-month-total">
            <div className="card-header bg-light py-2">
              <strong>{t("payroll.week_month_total")}</strong>
            </div>
            <PayrollMonthTable rows={rows} {...tableProps} testId="payroll-table" />
          </div>
        </>
      ) : (
        <div className="card card-soft">
          <PayrollMonthTable rows={rows} {...tableProps} testId="payroll-table" />
        </div>
      )}

      {isPrivileged && firedRows.length > 0 && (
        <div className="card card-soft mt-3" data-testid="payroll-fired-section">
          <div className="card-header bg-light d-flex align-items-center justify-content-between flex-wrap gap-2"
               style={{ cursor: "pointer" }}
               onClick={() => setShowFired(!showFired)}
               data-testid="payroll-fired-toggle">
            <div>
              <i className={`bi ${showFired ? "bi-caret-down-fill" : "bi-caret-right-fill"} me-2`}></i>
              <i className="bi bi-archive me-2 text-secondary"></i>
              <strong>{t("timesheet.fired_section")}</strong>
              <span className="badge bg-secondary ms-2">{t("timesheet.fired_count", { n: firedRows.length })}</span>
            </div>
            <div className="small text-muted d-none d-md-block">{t("timesheet.fired_section_hint")}</div>
          </div>
          {showFired && (
            <PayrollMonthTable
              rows={firedRows}
              {...tableProps}
              isPrivileged={false}
              fired
            />
          )}
        </div>
      )}
    </AppPage>
  );
}

function PayrollMonthTable({
  rows, t, company, employeeTaxes, employerTaxes, isPrivileged, totals, fired, testId,
}) {
  return (
    <div className="table-responsive">
      <table className="table align-middle mb-0 tabel-compact tabel-compact-tight payroll-nowrap" data-testid={testId}>
        <thead className="table-light">
          <tr>
            <th>{t("payroll.table.employee")}</th>
            <th className="text-end">{t("payroll.table.salary")}</th>
            <th className="text-end">{t("payroll.table.worked_days")}</th>
            <th className="text-end">{t("payroll.table.daily_rate")}</th>
            <th className="text-end">{t("payroll.table.gross")}</th>
            <th className="text-end">{t("payroll.table.bonus")}</th>
            <th className="text-end">{t("payroll.table.allowance")}</th>
            <th className="text-end">{t("payroll.table.total_gross")}</th>
            {employeeTaxes.map(x => (
              <th key={x.key} className="text-end" data-testid={testId ? `${testId}-th-${x.key}` : undefined}>{x.label}</th>
            ))}
            <th className="text-end text-danger">{t("payroll.table.withholding")}</th>
            <th className="text-end payroll-col-net">{t("payroll.table.net")}</th>
            {employerTaxes.map(x => (
              <th key={x.key} className="text-end text-muted">
                {x.label} <small>({t("payroll.table.employer_short")})</small>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(({ u, stats, dailyRate, tax, bonusTotal, allowanceTotal, withholdingTotal, netToPay }) => {
            const ss = (company?.social_statuses || []).find(s => s.id === u.social_status_id);
            return (
              <tr
                key={u.user_id}
                data-testid={fired ? `payroll-fired-row-${u.user_id}` : `payroll-row-${u.user_id}`}
                style={fired ? { background: "#fafafa" } : undefined}
              >
                <td>
                  {fired ? (
                    <>
                      <span className="text-muted" style={{ textDecoration: "line-through" }}>{u.name}</span>
                      <span className="badge bg-secondary ms-2">{t("timesheet.fired_badge")}</span>
                      {u.fired_at && (
                        <div className="small text-muted" style={{ fontSize: 11 }}>
                          {t("timesheet.fired_at", { date: new Date(u.fired_at).toLocaleDateString() })}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {u.name}
                      {ss && (
                        <span className="badge bg-info-subtle text-info-emphasis ms-1"
                              title={`${t("social_status.label")}: ${ss.name}`}
                              data-testid={`payroll-social-status-${u.user_id}`}
                              style={{ fontSize: 9 }}>
                          {ss.name}
                        </span>
                      )}
                    </>
                  )}
                </td>
                <td className="text-end" data-label={t("payroll.table.salary")}>
                  {fmt(u.salary)}
                  {!fired && u.salary_mode === "net" && (
                    <span className="badge bg-warning-subtle text-warning-emphasis ms-1"
                          style={{ fontSize: 9 }}>
                      {t("employees.salary_mode_badge_net")}
                    </span>
                  )}
                </td>
                <td className="text-end" data-label={t("payroll.table.worked_days")}>{stats.paidDays}</td>
                <td className="text-end" data-label={t("payroll.table.daily_rate")}>{fmt(dailyRate)}</td>
                <td className="text-end" data-label={t("payroll.table.gross")}>{fmt(tax.gross)}</td>
                <td className="text-end text-warning" data-label={t("payroll.table.bonus")}>
                  {bonusTotal > 0 ? fmt(bonusTotal) : "—"}
                </td>
                <td className="text-end text-primary" data-label={t("payroll.table.allowance")}>
                  {allowanceTotal > 0 ? fmt(allowanceTotal) : "—"}
                </td>
                <td className="text-end fw-semibold" data-label={t("payroll.table.total_gross")}>{fmt(tax.totalGross)}</td>
                {employeeTaxes.map(x => (
                  <td key={x.key} className="text-end" data-label={x.label}>{fmt(tax.byTax[x.key]?.amount || 0)}</td>
                ))}
                <td className="text-end text-danger" data-label={t("payroll.table.withholding")}>
                  {withholdingTotal > 0 ? `−${fmt(withholdingTotal)}` : "—"}
                </td>
                <td className="text-end fw-semibold text-success payroll-col-net" data-label={t("payroll.table.net")}>{fmt(netToPay)}</td>
                {employerTaxes.map(x => (
                  <td key={x.key} className="text-end text-muted" data-label={x.label}>{fmt(tax.byTax[x.key]?.amount || 0)}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
        {isPrivileged && rows.length > 0 && totals && (
          <tfoot className="table-light">
            <tr>
              <th>{t("payroll.table.total")}</th><th></th><th></th><th></th>
              <th className="text-end">{fmt(totals.gross)}</th>
              <th className="text-end text-warning">{fmt(totals.bonus)}</th>
              <th className="text-end text-primary">{fmt(totals.allowance)}</th>
              <th className="text-end">{fmt(totals.totalGross)}</th>
              {employeeTaxes.map(x => (
                <th key={x.key} className="text-end">{fmt(totals.byTax[x.key] || 0)}</th>
              ))}
              <th className="text-end text-danger">{totals.withholding > 0 ? `−${fmt(totals.withholding)}` : "—"}</th>
              <th className="text-end text-success payroll-col-net">{fmt(totals.net)}</th>
              {employerTaxes.map(x => (
                <th key={x.key} className="text-end">{fmt(totals.byTax[x.key] || 0)}</th>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
